import React, { lazy, Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import "react-datepicker/dist/react-datepicker.css";
import { fetchLeadById } from "../../store/slices/leadSlice";
import { FaPlus } from "react-icons/fa";
import LeadDetailsSkeleton from "../../components/Skeleton/LeadSkeleton";
import Button, { ButtonType } from "../../components/Assets/Button";
import AssigneeLogs from "../../components/OptimizedKanban/AssigneeLogs";
import { getAssigneeLogs } from "../../store/slices/kanbanSlice";
import { IAssigneeLog } from "../../models/assigneeLog.model";
import ILeadData from "../../models/LeadData.model";
import Remarks from "../TelecallerBoard/Remarks";
import { getRemarks } from "../../store/slices/telecallerLeadSlice";

const AddTask = lazy(() => import("./AddTask"));
const OngoingTasks = lazy(() => import("./OngoingTask"));
const CompletedTask = lazy(() => import("./CompletedTask/CompletedTask"));
const LeadInfo = lazy(() => import("./InfoLead"));
const LeadDetailsPage: React.FC = () => {
  const [isRightColumnVisible, setIsRightColumnVisible] = useState(false);
  const [logs, setLogs] = useState<IAssigneeLog[]>([]);
  const [followUpDate] = useState<Date | null>(null);

  const [card, setCard] = useState<ILeadData | null>(null);
  const { leadId } = useParams<{ leadId: string }>();

  const dispatch: RootDispatch = useDispatch();
  const lead = useSelector((state: RootState) => state.leadSlice.leads);
  const loading = useSelector((state: RootState) => state.leadSlice.loading);
  const remarks = useSelector(
    (state: RootState) => state.telecallerLead.remarks
  );
  const updateOutsideModal = useSelector(
    (state: RootState) => state.leadSlice.updateOutsideModal
  );

  const [remarkText, setRemarkText] = useState("");
  const [skipped, setSkipped] = useState(false);
  const [notInterested, setNotInterested] = useState(false);
  const [notPickedUp, setNotPickedUp] = useState(false);
  const [showToBDE, setShowToBDE] = useState<boolean | null>(null);
  const [agreedToMeet, setAgreedToMeet] = useState<boolean | null>(null);

  useEffect(() => {
    if (leadId) {
      dispatch(fetchLeadById(leadId));

      dispatch(getAssigneeLogs(leadId)).then((action: any) => {
        if (action.payload) {
          setLogs(action.payload);
        }
      });
    }
  }, [dispatch, leadId]);

  useEffect(() => {
    if (leadId) {
      dispatch(getRemarks(leadId));
    }
  }, [dispatch, leadId]);

  useEffect(() => {
    if (lead) {
      setCard(lead);
    }
  }, [lead]);

  if (loading && !updateOutsideModal) {
    return <LeadDetailsSkeleton />;
  }

  const FaPlusIcon = FaPlus as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  return (
    <div>
      <div className="flex flex-col lg:flex-row px-4 gap-6">
        <div className="w-full  lg:w-1/4">
          <Suspense fallback={<LeadDetailsSkeleton />}>
            <LeadInfo card={card!} />
          </Suspense>
        </div>

        <div className="w-full lg:w-2/4 p-2 md:p-6 bg-white shadow-lg rounded-lg transition-all duration-300 max-h-[90vh] overflow-y-auto">
          {/* Mobile Toggle Button */}
          <div className="md:hidden flex justify-center my-4">
            <Button
              type={ButtonType.Submit}
              onClick={() => setIsRightColumnVisible(!isRightColumnVisible)}
              className=" w-full"
            >
              <div className="flex text-xs items-center justify-center gap-2">
                <span>Add Task</span>
                <FaPlusIcon />
              </div>
            </Button>
          </div>

          <div
            className={`${isRightColumnVisible ? "block" : "hidden"} md:block`}
          >
            <h2 className="text-sm md:text-lg lg:text-2xl font-bold text-gray-800 mb-6 border-b mt-10 pb-2">
              Add Task
            </h2>
            <Suspense fallback={<LeadDetailsSkeleton />}>
              <AddTask
                leadId={leadId!}
                assigneeId={card?.assignee?._id}
                followUpCount={card?.followUpCount ?? 0}
              />
            </Suspense>
            <Suspense fallback={<LeadDetailsSkeleton />}>
              <OngoingTasks followUpDate={followUpDate!} leadData={card!} />
            </Suspense>
            <Suspense fallback={<LeadDetailsSkeleton />}>
              <CompletedTask />
            </Suspense>
          </div>
        </div>
        <div className="w-full  lg:w-1/4">
          <div className="flex flex-col gap-4">
            <Remarks
              remarks={remarks || []}
              leadId={leadId!}
              remarkText={remarkText}
              setRemarkText={setRemarkText}
              notInterested={notInterested}
              setNotInterested={setNotInterested}
              notPickedUp={notPickedUp}
              setNotPickedUp={setNotPickedUp}
              skipped={skipped}
              setSkipped={setSkipped}
              showToBDE={showToBDE}
              setShowToBDE={setShowToBDE}
              agreedToMeet={agreedToMeet}
              setAgreedToMeet={setAgreedToMeet}
              isTelecallerContext={false}
            />
          </div>
          {logs.length > 0 && (
            <div className=" p-4 bg-white shadow-lg rounded-lg transition-all duration-300 max-h-[280px] overflow-y-auto">
              <Suspense fallback={<div>Loading logs...</div>}>
                <AssigneeLogs logs={logs} />
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsPage;
